import { Handle, Position } from '@xyflow/react'
import { nodeControlClass } from '../../lib/nodeControlClass.js'
import { isFired } from './useFiredFlash'
import { selectedBorder } from './selectedBorder.js'

const ORANGE = '#e09030'

const TYPE_OPTIONS = ['animation', 'float', 'int', 'string']

const PIN_COLOR = {
  animation: '#e07040',
  float:     '#60b0d0',
  int:       '#60b0d0',
  string:    '#d060e8',
}

export function SelectNode({ data, selected }) {
  const fired       = isFired(data)
  const stop        = e => e.stopPropagation()
  const selectType  = data.selectType  ?? 'animation'
  const optionCount = data.optionCount ?? 2
  const index       = data.index       ?? 0
  const pinColor    = PIN_COLOR[selectType] ?? ORANGE
  const isAnim      = selectType === 'animation'

  const addOption    = () => data.onChange?.('optionCount', optionCount + 1)
  const removeOption = (i) => {
    if (optionCount <= 2) return
    data.onChange?.('optionCount', optionCount - 1)
  }

  return (
    <div style={{
      background: selected ? '#1e1608' : '#14100a',
      border: `2px solid ${fired ? '#ffcc44' : selected ? selectedBorder('#604010') : '#604010'}`,
      borderRadius: 6,
      minWidth: 220,
      fontFamily: 'var(--m-font)',
      fontSize: 12,
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
    }}>

      {/* ── Header ── */}
      <div style={{
        background: '#2a1a08',
        padding: '7px 14px 7px 20px',
        display: 'flex', alignItems: 'center', gap: 8,
        borderBottom: '1px solid #3a2010',
        borderRadius: '4px 4px 0 0',
        position: 'relative',
      }}>
        <span style={{ color: ORANGE, fontSize: 12, fontWeight: 'bold' }}>⊕</span>
        <span style={{ color: ORANGE, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Select</span>

        {/* Type selector */}
        <div style={{ marginLeft: 6, display: 'flex', gap: 0, borderRadius: 3, overflow: 'hidden', border: '1px solid #402010' }}>
          {TYPE_OPTIONS.map(t => (
            <button
              key={t}
              onMouseDown={stop}
              onClick={() => data.onChange?.('selectType', t)}
              style={{
                background: selectType === t ? '#402010' : 'transparent',
                border: 'none',
                color: selectType === t ? ORANGE : '#503020',
                padding: '2px 5px',
                fontSize: 8,
                fontFamily: 'var(--m-font)',
                cursor: 'pointer',
                letterSpacing: 0.2,
              }}
            >
              {t === 'animation' ? 'anim' : t}
            </button>
          ))}
        </div>

        {/* Return Value label + output handle */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}>
          <span style={{ color: '#604020', fontSize: 10 }}>Return Value</span>
          <Handle
            type="source"
            position={Position.Right}
            id={isAnim ? 'anim' : 'value'}
            style={{ background: pinColor, width: 10, height: 10, borderRadius: '50%' }}
          />
        </div>
      </div>

      {/* ── Options — wire-only, no inline editors ── */}
      {Array.from({ length: optionCount }, (_, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center',
          padding: '6px 12px 6px 20px',
          borderBottom: '1px solid #1e1208',
          position: 'relative',
          gap: 8,
        }}>
          <Handle
            type="target"
            position={Position.Left}
            id={`option-${i}`}
            style={{ background: pinColor, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${pinColor}55` }}
          />
          <span style={{ color: '#806040', fontSize: 10 }}>Option {i}</span>
          {optionCount > 2 && (
            <button
              onMouseDown={stop}
              onClick={() => removeOption(i)}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#402010', cursor: 'pointer', fontSize: 13, padding: '0 2px', lineHeight: 1 }}
            >×</button>
          )}
        </div>
      ))}

      {/* ── Add pin ── */}
      <div style={{ padding: '4px 14px', borderBottom: '1px solid #1e1208', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onMouseDown={stop}
          onClick={addOption}
          style={{
            background: 'none', border: 'none',
            color: '#604020', fontSize: 10,
            fontFamily: 'var(--m-font)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 4,
          }}
          onMouseEnter={e => e.currentTarget.style.color = ORANGE}
          onMouseLeave={e => e.currentTarget.style.color = '#604020'}
        >
          Add pin ⊕
        </button>
      </div>

      {/* ── Index ── */}
      <div style={{ padding: '6px 14px 8px 20px', display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
        <Handle
          type="target"
          position={Position.Left}
          id="in-index"
          style={{ background: '#60b0d0', width: 10, height: 10, borderRadius: '50%', border: '2px solid #60b0d044' }}
        />
        <span style={{ color: '#2a5060', fontSize: 10 }}>Index</span>
        <input
          className={nodeControlClass()}
          type="number"
          value={index}
          min={0}
          step={1}
          onChange={e => data.onChange?.('index', Math.max(0, parseInt(e.target.value) || 0))}
          onMouseDown={stop}
          style={{
            width: 48,
            background: '#0a0806',
            border: '1px solid #2a1808',
            color: '#c0a060',
            padding: '3px 6px',
            borderRadius: 3,
            fontSize: 10,
            fontFamily: 'var(--m-mono)',
            outline: 'none',
            textAlign: 'right',
          }}
        />
      </div>
    </div>
  )
}
